import {
    ChangeEvent,
    CSSProperties,
    JSX,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    useLayoutEffect,
} from 'react';
import { filter, get, includes, isEmpty } from 'lodash';
import { styled, css } from 'styled-components';

import Config from 'Config';
import {
    useLhsRiskTypesQuery,
    useWorkOrderRequirementApprovalReasonsQuery,
} from 'common/stores/redux/MetaStore/MetaStoreApi';
import Checkbox from 'common/components/form/CheckboxWrapper';
import Accordion from 'common/components/accordion/Accordion';
import * as logger from 'common/utils/Logger';
import Alert from 'common/components/Alert';
import EditPhotoRequirementsModal from 'admin/components/work-orders/photoRequirements/EditPhotoRequirementsModal';
import PhotoRequirementsApprovalModal from 'admin/components/work-orders/photoRequirements/PhotoRequirementsApprovalModal';
import MotiliTextButton from 'common/components/buttons/MotiliTextButton';
import { FormRow, FormElement } from 'common/components/form/FormLayout';
import TableActionsDropdown from 'common/components/table/TableActionsDropdown';
import {
    displayDateTime,
    displayDateTimeWithTimezone,
    toDateTimeObject,
} from 'common/utils/date-time';
import { useAppSelector } from 'common/stores/redux/hooks';
import { getUsagePhotoByDocIds } from 'admin/services/AdminPhotoService';
import DocumentsPhotoModal from 'common/components/modal/DocumentsPhotoModal';
import {
    DOCUMENT_TYPE,
    WorkOrderPhotoTypeAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    AcceptedFile,
    PhotoApproval,
    ApprovalReason,
    Document,
    PhotoTypeRequirement,
    QuotePhotoTypeRequirement,
    Photo,
    ACTION,
    APPROVAL_STATUS,
    LhsRiskType,
    IssueTracking,
} from 'common/types/documentRequirements';

import { Flex } from '@motili/ui';
import { getNotApplicationFiles } from 'common/components/file-upload/FileUploadDropzone';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import UploadPhotoRequireButton from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploadedRow/UploadPhotoRequireButton';
import { DocumentRequirementsPhotosUploadDrawer } from 'common/components/document/DocumentRequirementsPhotosDrawer';
import { PlayIconButton } from 'common/components/buttons/ActionIconButtons/PlayIconButton';
import { getSecuredURL } from 'common/services/S3StorageService';
import { VideoStatusId } from 'common/types/s3Storage';
import {
    KebabMenuButton,
    KebabMenuIcon,
} from 'common/components/buttons/ActionIconButtons/KebabMenuButton';
import { getAllowFileUpload } from 'common/components/file-upload/FileUploadTypes';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { DropZoneWithNA } from './DropZoneWithNA';
import {
    isVideo,
    photosSequenceSortFunc,
} from '../DocumentRequirementsPhotosUploaded';
import PhotoCardShell from '../../Shared/PhotoCardShell';
import styles from '../../DocumentRequirementsPhotos.module.scss';
import PhotoRequirementEmptyCard from '../../Shared/PhotoRequirementEmptyCard';
import PhotoViewDetailsDrawer from '../../Shared/PhotoViewDetailsDrawer';
import RejectedPhotoActions from '../../Shared/RejectedPhotoActions';
import { StatusChip, StyledChip } from '../../Shared/StyledChip';
import {
    getRequirementTypeDisplay,
    hasVideoRequirement,
    isAllowToEditRequirementType
} from 'common/utils/requirements/requirement-utils';

const AdminCardWrap = styled.div`
    width: 100%;
    min-width: 0;
    height: 100%;
    overflow: visible;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
`;

const InfoDotButton = styled(InfoOutlinedIcon)`
    cursor: pointer;
    font-size: 20px !important;
    color: ${Config.color.motiliLightBlue};
`;

const PreviewFrame = styled.div`
    position: relative;
    width: 100%;
    height: 100%;

    background: #000;
    overflow: hidden;

    border-radius: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    & img,
    & video,
    & canvas {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const BulkCheckbox = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;

    width: 28px;
    height: 28px;
    padding-top: 10px;
    padding-left: 16px;

    display: flex;
    align-items: center;
    justify-content: center;
`;

const BusyOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MetaRow = styled.div`
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
`;
const MetaLeft = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const ViewDetailsBtn = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: ${Config.color.motiliLightBlue};
`;

const ActionRow = styled.div`
    margin-top: 8px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
`;

const ActionBtn = styled.button<{ variant?: 'primary' | 'danger' }>`
    height: 32px;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    border: 1px solid ${Config.color.mediumLightGray};
    background: ${Config.color.white};
    color: ${Config.color.darkCharcoal};

    ${({ variant }) =>
        variant === 'primary'
            ? `
        background: ${Config.color.motiliLightBlue};
        color: #fff;
        border-color: ${Config.color.motiliLightBlue};
    `
            : ''}

    ${({ variant }) =>
        variant === 'danger'
            ? `
        border-color: ${Config.color.red};
        color: ${Config.color.red};
        background: #fff;
    `
            : ''}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const StatsGrid = styled.div`
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    font-size: 12px;
`;

const StatLabel = styled.div<{ $invalid?: boolean }>`
    color: ${({ $invalid }) =>
        $invalid ? Config.color.red : Config.color.darkCharcoal};
    font-weight: 700;
    font-size: 14px;
`;

const LabelValue = styled.div<{
    invalid?: boolean;
    pass?: boolean;
    $invalid?: boolean;
}>`
    margin-top: 2px;
    font-weight: 400;
    color: ${({ $invalid }) =>
        $invalid ? Config.color.red : Config.color.darkCharcoal};
`;

const StatValue = styled.div<{ invalid?: boolean; pass?: boolean }>`
    font-size: 16px;
    margin-top: 2px;
    font-weight: 400;
    color: ${({ invalid, pass }) =>
        invalid
            ? Config.color.red
            : pass
              ? Config.color.green
              : Config.color.darkCharcoal};
`;

const FailureBlock = styled.div`
    margin-top: 8px;
    font-size: 12px;
`;

const FailureValueRow = styled.div`
    margin-top: 4px;
    color: ${Config.color.red};
    font-weight: 400;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    text-align: left;
    gap: 6px;
    font-size: 16px;
`;

const ApprovalHistoryTop = styled.div`
    margin-top: 10px;

    :global(.accordionHeader),
    :global(.accordionTitle) {
        font-weight: 700;
        letter-spacing: 0.2px;
    }
`;

const EditFullWidthBtn = styled.button`
    width: 100%;
    height: 36px;
    margin-top: 8px;
    border: 1px solid ${Config.color.mediumLightGray};
    background: ${Config.color.white};
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const FullWidthEmptyItem = styled.div`
    flex: 0 0 100%;
    min-width: 100%;
    display: flex;
    justify-content: center;
`;

const AdminActionMenuWrap = styled.div`
    .no-uppercase {
        text-transform: none !important;
    }

    .remove-red,
    .remove-red a,
    .remove-red span {
        color: #e63232 !important;
    }

    .dropdown-menu > li > a,
    .dropdown-item,
    .no-uppercase {
        font-weight: 700 !important;
    }
`;

const StatusBulletList = styled.ul`
    margin: 0;
    padding-left: 18px;
    font-size: 13px;

    li + li {
        margin-top: 6px;
    }
`;

const CardRejectionTooltip = styled.div`
    display: none;
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 30;
    min-width: 220px;
    max-width: 300px;
    max-height: 200px;
    overflow-y: auto;
    pointer-events: auto;
`;

const CardStatusPillWrap = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;

    &:hover ${CardRejectionTooltip} {
        display: block;
    }
`;

const StatusInfoIcon = styled(InfoOutlinedIcon)<{ status?: string }>`
    font-size: 24px !important;
    margin-left: 4px;

    ${({ status }) => {
        const s = (status || '').toUpperCase();

        if (s.includes('APPROVE')) return `color: ${Config.color.green};`;
        if (s.includes('REJECT')) return `color: ${Config.color.red};`;
        if (s.includes('ACCEPT')) return `color: ${Config.color.localGray};`;
        return `color: ${Config.color.localGray};`;
    }}
`;

const CardTooltipBox = styled.div`
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.13);
    padding: 10px 14px;
    font-size: 12px;
    color: #222;
`;

const RejectedCtaDrawerWrap = styled.div`
    margin-top: 12px;
    display: flex;
    width: 100%;
    justify-content: flex-end;
    gap: 8px;
    align-items: center;
`;

export const RejectedCtaWrap = styled.div`
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    width: 100%;
`;

export const RejectedActionsWrap = styled.div`
    width: 100%;

    & > div {
        width: 100% !important;
        justify-content: space-between !important;
        gap: 8px !important;
    }

    & > div > div:first-child button {
        width: 100% !important;
        height: 40px !important;
    }

    & > div > div:last-child button {
        width: 100% !important;
        height: 40px !important;
    }

    button[data-test-id^='not-applicable'],
    button[data-test-id^='reupload'] {
        width: 100% !important;
        min-width: 0 !important;
    }
`;

const ClassificationRow = styled.div`
    margin-top: 8px;
    font-size: 12px;
`;

const ClassificationHeader = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 4px;
`;

const CleanDirtyBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #666;
`;

const BarTrack = styled.div`
    flex: 1;
    height: 6px;
    border-radius: 999px;
    background: #e0e0e0;
    overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $invalid?: boolean }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $invalid }) =>
        $invalid ? Config.color.red : Config.color.motiliLightBlue};
    border-radius: 999px;
`;

interface WorkOrderRequirementsPhotosUploadedRowProps {
    bulkApprovalSelected?: boolean;
    key: string;
    disabled?: boolean;
    removeAcls?: boolean;
    busy: boolean;
    photo: Photo;
    photos: Photo[];
    document: Document;
    handleSaveApprovalPhotoRequirement: (
        photoId: number,
        approval: Pick<PhotoApproval, 'approvalStatusId' | 'reasons'>
    ) => Promise<void>;
    photoTypes?: WorkOrderPhotoTypeAPI[];
    quotePhotoTypeRequirements: QuotePhotoTypeRequirement[];
    openRecommendationModal: (photoTypeId: string) => void;
    renderAdditionalNote: (photo: Photo) => JSX.Element;
    hasAdditionalNote: (photo: Photo) => boolean | string;
    handleEditOnClick: () => void;
    authenticationData: {
        authenticatedUserCanEdit: boolean;
        authenticatedUserIsCon: boolean;
    };
    handleOnDrop: (
        acceptedFiles: AcceptedFile[],
        rejectedFiles: File[],
        actionType: 'upload' | 'reupload'
    ) => void;
    handleMetadataOnClick: () => void;
    renderImage: () => JSX.Element | null;
    photoRequirementMet: (photo: Photo) => boolean;
    showReUpload: (photo: Photo) => boolean;
    handleCloseUpload: () => void;
    handleReUpload: (
        description: string,
        requiresPullAndClean: boolean
    ) => void;
    handleGetApprovalHistoryPhotoRequirement: (
        workOrderId: number,
        photoId: number
    ) => Promise<Photo[]>;
    photoTypeRequirements: PhotoTypeRequirement[];
    handleCloseOnClick: () => void;
    handleEditUploadedPhotoRequirement: (
        photoId: number,
        photoTypeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => void;
    showEditPhotoRequirementsModal: boolean;
    acceptedFiles: AcceptedFile[];
    action?: { display: string };
    handleRemovePhotoOnClick: () => Promise<void> | void;
    isRejected: (photo: Photo) => boolean;
    hasApprovalStatus: (photo: Photo) => boolean;
    cardWidth?: number;
    mediaHeight?: number;
    cardHeight?: number;
    selectPhotoBulkApproval?: (photoId: ChangeEvent<HTMLInputElement>) => void;
    videoDuration?: string;
}

const WorkOrderRequirementsPhotosUploadedRow = ({
    selectPhotoBulkApproval,
    bulkApprovalSelected,
    key,
    disabled,
    removeAcls,
    photo,
    photos,
    document,
    handleSaveApprovalPhotoRequirement,
    photoTypes,
    videoTypes,
    quotePhotoTypeRequirements,
    handleEditOnClick,
    authenticationData,
    handleOnDrop,
    renderImage,
    photoRequirementMet,
    videoRequirementMet,
    showReUpload,
    handleCloseUpload,
    handleReUpload,
    handleGetApprovalHistoryPhotoRequirement,
    photoTypeRequirements,
    videoTypeRequirements,
    handleCloseOnClick,
    handleEditUploadedPhotoRequirement,
    showEditPhotoRequirementsModal,
    acceptedFiles,
    action,
    handleRemovePhotoOnClick,
    isRejected,
    busy,
    hasApprovalStatus,
    cardWidth,
    mediaHeight,
    cardHeight,
    videoDuration,
    requirementTypeId,
}: WorkOrderRequirementsPhotosUploadedRowProps) => {
    const { data: workOrderRequirementApprovalReasons } =
        useWorkOrderRequirementApprovalReasonsQuery();
    const { data: lhsRiskTypes } = useLhsRiskTypesQuery();
    const { isClientEnablePhotoVerification } = useAppSelector(
        state => state.adminWorkOrder
    );

    const { isMotiliAccount, isContractorAccount } = useAppSelector(
        state => state.authentication
    );

    const [isBusy, setBusy] = useState(false);
    const [approvalHistory, setApprovalHistory] = useState<any[] | null>(null);
    const [expandApprovalHistoryCard, setExpandApprovalHistoryCard] =
        useState(false);
    const [expandApprovalHistoryDrawer, setExpandApprovalHistoryDrawer] =
        useState(false);
    const [approvalStatus, setApprovalStatus] = useState<string>('');
    const [dupModalData, setDupModalData] = useState<{ refDocs: any[] } | null>(
        null
    );

    const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
    const [isMetadataOpen, setIsMetadataOpen] = useState(false);
    const [metadataData, setMetadataData] = useState<any>(null);
    const [pendingOpenInstances, setPendingOpenInstances] = useState(false);
    const [instancesLoading, setInstancesLoading] = useState(false);
    const [isDrawerRemoving, setIsDrawerRemoving] = useState(false);
    const showMetadataLink = useMemo(
        () => Boolean(photo.photoMetadata) || Boolean(photo.videoMetadata),
        [photo]
    );
    const openEditFromViewDetails = () => {
        setIsViewDetailsOpen(false);
        window.setTimeout(() => {
            handleEditOnClick();
        }, 250);
    };

    const openInstancesFromViewDetails = () => {
        setPendingOpenInstances(true);
        setIsViewDetailsOpen(false);
    };
    const handleRemoveFromDrawer = async () => {
        setIsDrawerRemoving(true);
        try {
            await Promise.resolve(handleRemovePhotoOnClick());
        } finally {
            setIsDrawerRemoving(false);
            setIsViewDetailsOpen(false);
        }
    };

    const onMetadataClick = () => {
        const md = isVideo(photo)
            ? photo.videoMetadata ||
              (photo.requirement as unknown as Photo)?.videoMetadata
            : photo.photoMetadata ||
              (photo.requirement as unknown as Photo)?.photoMetadata;
        setMetadataData(md);
        setIsMetadataOpen(true);
    };

    useEffect(() => {
        setIsViewDetailsOpen(false);
    }, [photo?.id]);

    useEffect(() => {
        if (isViewDetailsOpen) return;
        if (!pendingOpenInstances) return;

        setPendingOpenInstances(false);

        (async () => {
            try {
                setInstancesLoading(true);
                const refDocs = await fetchDuplicateRefDocs(
                    photo.photoIntegrity as any
                );
                setDupModalData({ refDocs });
            } finally {
                setInstancesLoading(false);
            }
        })();
    }, [isViewDetailsOpen, pendingOpenInstances, photo.photoIntegrity]);

    const showApprovalRejectButton = useCallback(
        (photo: Photo) => {
            const showStatus = ['IN_REVIEW'];

            return (
                document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
                showStatus.includes(document.statusId) &&
                (isClientEnablePhotoVerification || isRejected(photo))
            );
        },
        [document, isClientEnablePhotoVerification, isRejected]
    );

    const handleApprovePhoto = async (photo: Photo) => {
        const systemValid =
            (!photo.photoPrediction?.classification ||
                photo.photoPrediction?.classification?.validClassification) &&
            (!photo.photoPrediction?.identification ||
                photo.photoPrediction?.identification?.validIdentification) &&
            (!photo.photoIntegrity || photo.photoIntegrity?.validIntegrity);

        if (systemValid) {
            const approval: Pick<
                PhotoApproval,
                'approvalStatusId' | 'reasons'
            > = {
                approvalStatusId: 'APPROVE',
                reasons: [],
            };

            setBusy(true);
            try {
                await handleSaveApprovalPhotoRequirement(photo.id, approval);
                setBusy(false);
                clearApprovalHistory();
                try {
                    const _approvalHistory =
                        await handleGetApprovalHistoryPhotoRequirement(
                            document.id,
                            photo.id
                        );
                    setApprovalHistory(_approvalHistory);
                } catch (error) {
                    logger.error(error);
                }
            } catch (error: any) {
                logger.error(error);
                Alert.error(
                    `Sorry, issue approval photo requirement: ${error.message}`
                );
                setBusy(false);
            } finally {
                setIsViewDetailsOpen(false);
            }
        } else {
            handleModalOpen('APPROVE');
        }
    };

    const handleModalOpen = (statusId: string) => {
        setApprovalStatus(statusId);
    };

    const handleModalClose = async () => {
        setApprovalStatus('');
        clearApprovalHistory();
        try {
            const _approvalHistory =
                await handleGetApprovalHistoryPhotoRequirement(
                    document.id,
                    photo.id
                );
            setApprovalHistory(_approvalHistory);
        } catch (error) {
            logger.error(error);
        }
    };

    const hasApprovalHistory = (photo: Photo) => {
        return photoRequirementMet(photo) && photo?.approvalVersion > 0;
    };

    const handleExpandedApprovalHistoryCard = (
        photo: { id: number },
        expand: boolean | ((prev: boolean) => boolean)
    ) => {
        setExpandApprovalHistoryCard(expand);

        if (!approvalHistory) {
            setBusy(true);
            return handleGetApprovalHistoryPhotoRequirement(
                document.id,
                photo.id
            )
                .then(_approvalHistory => setApprovalHistory(_approvalHistory))
                .catch(error => logger.error(error))
                .finally(() => setBusy(false));
        }
    };

    const handleExpandedApprovalHistoryDrawer = (
        photo: { id: number },
        expand: boolean | ((prev: boolean) => boolean)
    ) => {
        setExpandApprovalHistoryDrawer(expand);

        if (!approvalHistory) {
            setBusy(true);
            return handleGetApprovalHistoryPhotoRequirement(
                document.id,
                photo.id
            )
                .then(_approvalHistory => setApprovalHistory(_approvalHistory))
                .catch(error => logger.error(error))
                .finally(() => setBusy(false));
        }
    };

    const clearApprovalHistory = () => {
        setApprovalHistory(null);
        setExpandApprovalHistoryCard(false);
        setExpandApprovalHistoryDrawer(false);
    };

    const renderApprovalHistory = (
        photo: Photo,
        approvalHistory: Photo[] | null,
        mode: 'card' | 'drawer'
    ) => {
        const isExpand =
            mode === 'drawer'
                ? expandApprovalHistoryDrawer
                : expandApprovalHistoryCard;

        const onExpand =
            mode === 'drawer'
                ? (expand: any) =>
                      handleExpandedApprovalHistoryDrawer(photo, expand)
                : (expand: any) =>
                      handleExpandedApprovalHistoryCard(photo, expand);

        // ---- DRAWER mode ----
        if (mode === 'drawer') {
            return (
                <div style={{ marginTop: 8, width: '100%' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            color: Config.color.motiliLightBlue,
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                        onClick={() => onExpand(!isExpand)}
                    >
                        Approval History
                        <i
                            className={`fa fa-chevron-${isExpand ? 'up' : 'down'}`}
                            style={{ fontSize: 12 }}
                        />
                    </div>
                    {isExpand && (
                        <div
                            style={{
                                marginTop: 8,
                                border: '1px solid #e0e0e0',
                                borderRadius: 4,
                                maxHeight: 240,
                                overflowY: 'auto',
                                padding: '8px 12px',
                            }}
                        >
                            {(approvalHistory ?? []).map((history: Photo) => (
                                <ApprovalHistoryDetail
                                    key={history.id}
                                    photo={history}
                                    approvalReasons={
                                        workOrderRequirementApprovalReasons as ApprovalReason[]
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // ---- CARD mode ----
        return (
            <div style={{ marginTop: 8 }}>
                <FormRow>
                    <FormElement>
                        <Accordion
                            title='Latest Update'
                            headerBackgroundColor='transparent'
                            headerColor={Config.color.motiliLightBlue}
                            headerNoSpace
                            noBorder
                            id={`approval-history-card-${photo.id}`}
                            key={`approval-history-card-${photo.id}`}
                            isExpand={isExpand}
                            handleExpanded={onExpand}
                        >
                            {(approvalHistory ?? [])
                                .slice(0, 1)
                                .map((history: Photo) => (
                                    <ApprovalHistoryDetail
                                        key={history.id}
                                        photo={history}
                                        approvalReasons={
                                            workOrderRequirementApprovalReasons as ApprovalReason[]
                                        }
                                    />
                                ))}
                        </Accordion>
                    </FormElement>
                </FormRow>
            </div>
        );
    };

    let issueTracking: IssueTracking | undefined;
    if (photo?.requirement?.exceptionFlagId) {
        issueTracking = document.issueTracking?.find(
            issue => issue.id === photo.requirement.exceptionFlagId
        );
    }

    const { isLHS } = useMemo(() => {
        const isLHS =
            document?.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            photo?.requirement?.photoTypeId === 'LIFE_HEALTH_SAFETY_RISK';

        return {
            isLHS,
        };
    }, [document?.documentTypeId, photo?.requirement?.photoTypeId]);

    let riskType: LhsRiskType | undefined;
    let riskReasons: (string | undefined)[] | undefined;

    if (issueTracking) {
        riskType =
            issueTracking.issueReasonId.length > 0 &&
            issueTracking.issueReasonId[0]
                ? lhsRiskTypes?.find(
                      rType => rType.id === issueTracking?.issueReasonId[0]
                  )
                : undefined;

        const _rawReasons =
            riskType &&
            [...(issueTracking?.issueSecondaryReasonId ?? [])]?.sort((a, b) => {
                if (a === 'OTHER') return 1;
                if (b === 'OTHER') return -1;
                if (a < b) return -1;
                if (a > b) return 1;
                return 0;
            });
        riskReasons =
            riskType &&
            _rawReasons?.map(reason =>
                reason === 'OTHER'
                    ? issueTracking?.issueOtherReason
                    : riskType?.reasons[`${reason}`]?.display
            );
    } else {
        riskType = lhsRiskTypes
            ? lhsRiskTypes.find(
                  rType => rType.id === document.workOrderLHS?.[0]?.riskType
              )
            : undefined;
        const _rawReasons =
            riskType && document.workOrderLHS?.[0]?.riskReasons
                ? [...document.workOrderLHS[0].riskReasons].sort((a, b) => {
                      if (a === 'OTHER') return 1;
                      if (b === 'OTHER') return -1;
                      if (a < b) return -1;
                      if (a > b) return 1;
                      return 0;
                  })
                : undefined;
        riskReasons =
            riskType &&
            _rawReasons?.map(reason =>
                reason === 'OTHER'
                    ? document.workOrderLHS &&
                      document.workOrderLHS?.[0]?.otherReason
                    : riskType!.reasons[`${reason}`]?.display
            );
    }

    const photoType = ([...photoTypes, ...videoTypes] || []).find(
        pt => pt.id === photo.requirement.photoTypeId
    );
    const isImage =
        photo.requirement.mimeType === 'image/png' ||
        photo.requirement.mimeType === 'image/jpeg';
    const photoIdentification = photo.photoPrediction
        ? photo.photoPrediction?.identification?.validIdentification
            ? 'PASS'
            : 'FAIL'
        : 'N/A';
    const disabledActionFromStatus = getIsActionDisabledFromStatus({
        document,
        isMotiliAccount,
        isContractorAccount,
    });
    const isNoPhotoTerminalStatus = isTerminalStatus(document);
    const allowUpload = allowUploadPhoto({
        document,
        isMotiliAccount,
        isContractorAccount,
    });
    const showApprovalButtons =
        photoRequirementMet(photo) &&
        showApprovalRejectButton(photo) &&
        !hasApprovalStatus(photo) &&
        !isVideo(photo);

    const showEditApproval =
        photoRequirementMet(photo) &&
        showApprovalRejectButton(photo) &&
        hasApprovalStatus(photo) &&
        !isVideo(photo);
    const showUploadButton = !photo.requirement.url;
    const showReUploadButtons =
        photoRequirementMet(photo) &&
        hasApprovalStatus(photo) &&
        showReUpload(photo);

    const hasUploaded = Boolean(photo?.requirement?.mimeType);
    const isEmptyUploadCard = !hasUploaded;

    const thisCategory = getMediaCategory(photo);
    const isDocument =
        photo?.requirement?.mimeType &&
        !photo.requirement.mimeType.startsWith('image/') &&
        !photo.requirement.mimeType.startsWith('video/');

    const uploadedSameTypeAndCategory = (photos || []).filter(
        p =>
            p?.requirement?.photoTypeId === photo?.requirement?.photoTypeId &&
            Boolean(p?.requirement?.url) &&
            getMediaCategory(p) === thisCategory // group by media category
    );

    const sortedPhotoSameTypeAndCategory = uploadedSameTypeAndCategory?.sort(
        photosSequenceSortFunc
    );

    const idx = Math.max(
        0,
        sortedPhotoSameTypeAndCategory.findIndex(p => p.id === photo.id)
    );
    const photoIndexLabel = `${isVideo(photo) ? 'Video' : isDocument ? 'Document' : 'Photo'} #${idx + 1}`;

    const approvalStatusId = photo.approvalStatusId || '';

    const toTitleCase = (str: string) =>
        str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    const statusDisplay = approvalStatusId
        ? toTitleCase(
              (APPROVAL_STATUS as any)?.[approvalStatusId]?.display ||
                  approvalStatusId
          )
        : '';

    const onViewDetailsClick = () => setIsViewDetailsOpen(true);
    const allowFiles = useMemo(
        () => getAllowFileUpload(
            document?.client?.enabledVideoRecording,
            hasVideoRequirement(document?.clientRequirements) ||
            hasVideoRequirement(document?.projectRequirements),
            true,
            requirementTypeId),
        [document, requirementTypeId]
    );

    const kebab = getShowKebab({
        photo,
        disabled,
        disabledActionFromStatus,
        authenticatedUserCanEdit: authenticationData.authenticatedUserCanEdit,
        removeAcls,
        busy,
    }) ? (
        <AdminActionMenuWrap>
            <TableActionsDropdown
                title='Photo Requirement Table Actions'
                icon={<KebabMenuIcon />}
                actions={[
                    ...(isAllowToEditRequirementType(photo,
                        document?.client?.enabledVideoRecording ||
                        hasVideoRequirement(document?.clientRequirements) ||
                        hasVideoRequirement(document?.projectRequirements)
                    )
                        ? [
                              {
                                  name: `Update ${getRequirementTypeDisplay(photo.requirementTypeId)} Type`,
                                  onClick: () => openEditFromViewDetails(),
                                  acls: removeAcls,
                                  className: 'no-uppercase',
                              },
                          ]
                        : []),
                    {
                        name: 'Remove',
                        onClick: () => handleRemovePhotoOnClick(),
                        acls: removeAcls,
                        className: `${styles.remove} no-uppercase remove-red`,
                    },
                ]}
            />
        </AdminActionMenuWrap>
    ) : null;

    const integrityFail = photo.photoIntegrity?.validIntegrity === false;
    const failReasons: string[] = [];
    if (integrityFail) {
        if (photo.photoIntegrity?.validateDuplication?.validDuplicate === false)
            failReasons.push('Reused photo');
        if (
            photo.photoIntegrity?.validateCreationDate?.validCreationDate ===
            false
        )
            failReasons.push('Photo outside timeframe');
        if (photo.photoIntegrity?.validateLocation?.validLocation === false)
            failReasons.push('Photo outside radius');
    }
    const failReasonText = failReasons[0] || 'Fail';

    const handleViewInstances = useCallback(async () => {
        const refDocs = await fetchDuplicateRefDocs(
            photo.photoIntegrity as any
        );
        setDupModalData({ refDocs });
    }, [photo.photoIntegrity]);

    const integrityDetails = useMemo(
        () =>
            getIntegrityDetails(
                photo.photoIntegrity,
                photo.photoMetadata,
                document.property?.timezone
            ),
        [photo.photoIntegrity, photo.photoMetadata, document.property?.timezone]
    );

    const headerActions = (
        <Flex gap='8px' align='center'>
            {showApprovalButtons && (
                <>
                    <MotiliTextButton
                        disabled={disabledActionFromStatus || busy}
                        text='APPROVE'
                        onClick={() => handleApprovePhoto(photo)}
                    />
                    <MotiliTextButton
                        disabled={disabledActionFromStatus || busy}
                        text='REJECT'
                        color={Config.color.red}
                        onClick={() => handleModalOpen('REJECT')}
                    />
                    <MotiliTextButton
                        disabled={disabledActionFromStatus || busy}
                        text='ACCEPT'
                        color={Config.color.darkCharcoal}
                        onClick={() => handleModalOpen('ACCEPT')}
                    />
                </>
            )}

            {showEditApproval && (
                <MotiliTextButton
                    disabled={disabledActionFromStatus || busy}
                    text='EDIT'
                    onClick={() =>
                        handleModalOpen(photo.approvalStatusId || '')
                    }
                />
            )}

            {showUploadButton && (
                <DropZoneWithNA
                    id='dropzone-upload'
                    handleOnDrop={(acceptedFiles, rejectedFiles) =>
                        handleOnDrop(acceptedFiles, rejectedFiles, 'upload')
                    }
                    disabled={disabledActionFromStatus || !allowUpload || busy}
                    text='UPLOAD'
                />
            )}

            {showReUploadButtons && (
                <DropZoneWithNA
                    id='dropzone-re-upload'
                    handleOnDrop={(acceptedFiles, rejectedFiles) =>
                        handleOnDrop(acceptedFiles, rejectedFiles, 'reupload')
                    }
                    disabled={disabledActionFromStatus || busy}
                    text='RE-UPLOAD'
                />
            )}

            {photo.requirement.url && (
                <AdminActionMenuWrap>
                    <TableActionsDropdown
                        busy={busy}
                        disabled={
                            disabled ||
                            disabledActionFromStatus ||
                            !authenticationData.authenticatedUserCanEdit ||
                            removeAcls === false
                        }
                        title='Photo Requirement Table Actions'
                        icon={<KebabMenuIcon />}
                        actions={[
                            {
                                name: `Update ${requirementTypeId === 'PHOTO' ? 'Photo' : 'Video'} Type`,
                                onClick: () => handleEditOnClick(),
                                acls: removeAcls,
                                className: 'no-uppercase',
                            },
                            {
                                name: 'Remove',
                                onClick: () => handleRemovePhotoOnClick(),
                                acls: removeAcls,
                                className: `${styles.remove} no-uppercase remove-red`,
                            },
                        ]}
                    />
                </AdminActionMenuWrap>
            )}
        </Flex>
    );

    const photoRequirementActions = useMemo(() => {
        if (!showReUploadButtons) return null;

        return (
            <RejectedCtaDrawerWrap>
                <MotiliButton
                    dataTestId={`not-applicable-${photo.id}`}
                    text='Not Applicable'
                    moStyle='normal'
                    disabled={disabledActionFromStatus || !allowUpload || busy}
                    style={{ width: 144, height: 36, whiteSpace: 'nowrap' }}
                    onClick={() => {
                        const files = getNotApplicationFiles();
                        handleOnDrop(files as any, [], 'reupload');
                    }}
                />
                <UploadPhotoRequireButton
                    dataTestId={`reupload-${photo.id}`}
                    handleOnDrop={(acceptedFiles, rejectedFiles) => {
                        setIsViewDetailsOpen(false);
                        return handleOnDrop(
                            acceptedFiles,
                            rejectedFiles,
                            'reupload'
                        );
                    }}
                    disabled={disabledActionFromStatus || busy}
                    text='Re-Upload'
                    allowFiles={allowFiles}
                    buttonStyle={{ width: 113, height: 36, padding: 0 }}
                />
            </RejectedCtaDrawerWrap>
        );
    }, [
        showReUploadButtons,
        busy,
        disabledActionFromStatus,
        allowUpload,
        handleOnDrop,
        photo.id,
        allowFiles,
    ]);

    const details = getStatusDetails(
        photo,
        workOrderRequirementApprovalReasons || []
    );
    const showInfo = details.length > 0;

    const drawerKebab =
        photo.requirement.url &&
        !disabled &&
        !disabledActionFromStatus &&
        authenticationData.authenticatedUserCanEdit &&
        removeAcls !== false ? (
            <AdminActionMenuWrap>
                <TableActionsDropdown
                    title='Photo Requirement Drawer Actions'
                    icon={<KebabMenuIcon />}
                    actions={[
                        ...(isAllowToEditRequirementType(photo,
                            document?.client?.enabledVideoRecording ||
                            hasVideoRequirement(document?.clientRequirements) ||
                            hasVideoRequirement(document?.projectRequirements)
                        )
                            ? [
                                  {
                                      name: `Update ${getRequirementTypeDisplay(photo.requirementTypeId)} Type`,
                                      onClick: () => openEditFromViewDetails(),
                                      acls: removeAcls,
                                      className: 'no-uppercase',
                                  },
                              ]
                            : []),
                        {
                            name: 'Remove',
                            onClick: () => handleRemoveFromDrawer(),
                            acls: removeAcls,
                            className: `${styles.remove} no-uppercase remove-red`,
                        },
                    ]}
                />
            </AdminActionMenuWrap>
        ) : null;

    return (
        <>
            <PhotoViewDetailsDrawer
                open={isViewDetailsOpen}
                onClose={() => {
                    setIsMetadataOpen(false);
                    setIsViewDetailsOpen(false);
                }}
                mode='admin'
                title={
                    photoType?.display ||
                    photo.requirement?.display ||
                    'View Details'
                }
                photo={photo}
                renderImage={renderImage}
                headerRight={drawerKebab}
                showCheck={photo?.requirementTypeId === 'VIDEO' ? videoRequirementMet(photo) : photoRequirementMet(photo)}
                showMetadataLink={showMetadataLink}
                onMetadataClick={onMetadataClick}
                metadataPanel={{
                    open: isMetadataOpen,
                    onClose: () => setIsMetadataOpen(false),
                    metadata: metadataData,
                    photoIntegrity: photo.photoIntegrity,
                    document,
                }}
                workOrderRequirementApprovalReasons={
                    workOrderRequirementApprovalReasons
                }
                isLHS={isLHS}
                riskType={riskType}
                riskReasons={riskReasons}
                cardMeta={{
                    photoIndexLabel,
                    statusDisplay,
                    approvalStatusId,
                    photoIdentification,
                    integrityFail,
                    integrityText: photo.photoIntegrity
                        ? photo.photoIntegrity.validIntegrity
                            ? 'Pass'
                            : 'Fail'
                        : 'N/A',
                    failReasonText,
                    showFailureInstancesBtn:
                        photo.photoIntegrity?.validateDuplication
                            ?.validDuplicate === false,
                    integrityDetails,
                    showApprovalButtons,
                    showEditApproval,
                    hasApprovalHistory: hasApprovalHistory(photo),
                    approvalHistoryNode: renderApprovalHistory(
                        photo,
                        approvalHistory,
                        'drawer'
                    ),
                    expandApprovalHistory: expandApprovalHistoryDrawer,
                    requiresPullAndClean:
                        photo.requirement?.requiresPullAndClean,
                }}
                adminActions={{
                    disabled:
                        disabledActionFromStatus ||
                        busy ||
                        instancesLoading ||
                        isDrawerRemoving,
                    busy: busy || instancesLoading || isDrawerRemoving,
                    onApprove: () => handleApprovePhoto(photo),
                    onReject: () => handleModalOpen('REJECT'),
                    onAccept: () => handleModalOpen('ACCEPT'),
                    onEdit: () => handleModalOpen(photo.approvalStatusId || ''),
                    onViewInstances: openInstancesFromViewDetails,
                    onToggleApprovalHistory: (expand: boolean) =>
                        handleExpandedApprovalHistoryDrawer(photo, expand),
                }}
                photoRequirementActions={photoRequirementActions}
                showStats={document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER}
                videoDuration={videoDuration}
                drawerBusy={isDrawerRemoving}
            />

            {acceptedFiles.length > 0 && (
                <DocumentRequirementsPhotosUploadDrawer
                    open
                    handleCloseOnClick={() => handleCloseUpload()}
                    handleCancelOnClick={() => handleCloseUpload()}
                    handleUploadOnClick={({
                        typeId,
                        description,
                        requiresPullAndClean,
                    }: {
                        typeId?: string;
                        description: string;
                        requiresPullAndClean: boolean;
                    }) => {
                        handleReUpload(description, requiresPullAndClean);
                        setIsViewDetailsOpen(false);
                    }}
                    photoTypes={photoTypes}
                    videoTypes={videoTypes}
                    currentPhotoTypeId={photo.requirement.photoTypeId}
                    photoTypeRequirements={photoTypeRequirements}
                    videoTypeRequirements={videoTypeRequirements}
                    photos={photos}
                    file={acceptedFiles[0]}
                    fileName={acceptedFiles[0].name}
                    imgSrc={acceptedFiles[0].preview}
                    mimeType={acceptedFiles[0].type}
                    currentRequiresPullAndClean={get(photo, [
                        'requirement',
                        'requiresPullAndClean',
                    ])}
                    action={action}
                    requirementTypeId={requirementTypeId}
                    enabledVideoRecording={document?.client?.enabledVideoRecording}
                    hasVideoRequirement={
                        hasVideoRequirement(document?.clientRequirements) ||
                        hasVideoRequirement(document?.projectRequirements)
                    }
                />
            )}

            {showEditPhotoRequirementsModal && (
                <EditPhotoRequirementsModal
                    photos={photos || []}
                    selectedPhoto={photo}
                    currentPhotoTypeId={photoType?.id}
                    handleClose={() => handleCloseOnClick()}
                    handleEditUploadedPhotoRequirement={async (...args) => {
                        await handleEditUploadedPhotoRequirement(...args);
                        const _approvalHistory =
                            await handleGetApprovalHistoryPhotoRequirement(
                                document.id,
                                photo.id
                            );
                        setApprovalHistory(_approvalHistory);
                    }}
                    photoTypeRequirements={photoTypeRequirements}
                    quotePhotoTypeRequirements={quotePhotoTypeRequirements}
                    mimeType={photo.requirement.mimeType}
                    currentRequiresPullAndClean={
                        photo.requirement.requiresPullAndClean
                    }
                    renderImage={renderImage}
                    videoDuration={videoDuration}
                />
            )}

            {approvalStatus && (
                <PhotoRequirementsApprovalModal
                    photo={photo}
                    handleClose={() => handleModalClose()}
                    handleSave={(...params) => {
                        clearApprovalHistory();
                        setApprovalStatus('');
                        setIsViewDetailsOpen(false);
                        return handleSaveApprovalPhotoRequirement(...params);
                    }}
                    clearHistory={() => clearApprovalHistory()}
                    initialType={approvalStatus}
                />
            )}

            {dupModalData && (
                <DocumentsPhotoModal
                    handleCloseOnClick={() => setDupModalData(null)}
                    documents={dupModalData.refDocs}
                    metadata={photo.photoMetadata}
                />
            )}

            {isEmptyUploadCard ? (
                <FullWidthEmptyItem data-test-id='admin-photo-empty-item'>
                    {isNoPhotoTerminalStatus ? (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                padding: '16px 8px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                background:
                                    'var(--color-super-light-gray, #F5F5F5)',
                                color: Config.color.darkCharcoal,
                                fontSize: 18,
                                fontWeight: 600,
                            }}
                        >
                            {`No ${getRequirementTypeDisplay(requirementTypeId).toLowerCase()}s have been uploaded`}
                        </div>
                    ) : (
                        <PhotoRequirementEmptyCard
                            dataTestId='admin-photo-empty-bar'
                            showHint
                            uploadAction={
                                <UploadPhotoRequireButton
                                    dataTestId='upload'
                                    handleOnDrop={(
                                        acceptedFiles,
                                        rejectedFiles
                                    ) =>
                                        handleOnDrop(
                                            acceptedFiles,
                                            rejectedFiles,
                                            'upload'
                                        )
                                    }
                                    disabled={!allowUpload || busy}
                                    text='Upload'
                                    size='sm'
                                    allowFiles={allowFiles}
                                    buttonStyle={{
                                        width: '120px',
                                        height: '40px',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                    }}
                                />
                            }
                            notApplicableAction={
                                <MotiliButton
                                    dataTestId='upload-not-applicable'
                                    text='Not Applicable'
                                    size='sm'
                                    moStyle='normal'
                                    disabled={!allowUpload || busy}
                                    style={{
                                        width: '158px',
                                        height: '40px',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                    }}
                                    onClick={() => {
                                        const files = getNotApplicationFiles();
                                        handleOnDrop(
                                            files as any,
                                            [],
                                            'upload'
                                        );
                                    }}
                                />
                            }
                            title={`No ${getRequirementTypeDisplay(requirementTypeId).toLowerCase()}s have been uploaded`}
                            hint ={`At least 1 ${getRequirementTypeDisplay(requirementTypeId)} Required`}
                            requirementTypeId={requirementTypeId}
                        />
                    )}
                </FullWidthEmptyItem>
            ) : (
                <AdminCardWrap>
                    <PhotoCardShell
                        dataTestId='admin-photo-card'
                        headerRight={null}
                        kebab={null}
                        headerStyle={{ padding: 0, minHeight: 0 }}
                        bodyStyle={{ padding: 10, overflow: 'visible' }}
                        cardWidth={cardWidth}
                        mediaHeight={mediaHeight}
                        cardHeight={cardHeight}
                        media={
                            <PreviewFrame style={{ height: '100%' }}>
                                {showApprovalButtons &&
                                    selectPhotoBulkApproval && (
                                        <BulkCheckbox>
                                            <Checkbox
                                                id={`bulk-checkbox-${key}`}
                                                checked={bulkApprovalSelected}
                                                onChange={e =>
                                                    selectPhotoBulkApproval(e)
                                                }
                                                disabled={disabled}
                                                style={{ outline: 'none' }}
                                            />
                                        </BulkCheckbox>
                                    )}

                                {kebab && (
                                    <KebabMenuButton>{kebab}</KebabMenuButton>
                                )}
                                {busy && !isDrawerRemoving && (
                                    <BusyOverlay>
                                        <i
                                            className='fa fa-spinner fa-spin'
                                            style={{
                                                fontSize: 28,
                                                color: '#fff',
                                            }}
                                        />
                                    </BusyOverlay>
                                )}

                                <a
                                    href={
                                        isVideo(photo)
                                            ? undefined
                                            : photo.requirement.url
                                    }
                                    onClick={() => {
                                        if (isCompleteVideo(photo)) {
                                            openVideoInNewTab(photo);
                                        }
                                    }}
                                    target='_blank'
                                    rel='noreferrer'
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        height: '100%',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor:
                                            !isVideo(photo) ||
                                            isCompleteVideo(photo)
                                                ? 'pointer'
                                                : 'default',
                                    }}
                                >
                                    <VideoMask
                                        photo={photo}
                                        duration={videoDuration}
                                    />
                                    {renderImage()}
                                </a>
                            </PreviewFrame>
                        }
                    >
                        <MetaRow>
                            <MetaLeft>
                                <StyledChip style={{ whiteSpace: 'nowrap' }}>
                                    {photoIndexLabel}
                                </StyledChip>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {hasApprovalStatus(photo) &&
                                        approvalStatusId && (
                                            <CardStatusPillWrap>
                                                <StatusChip
                                                    status={approvalStatusId}
                                                >
                                                    {statusDisplay}
                                                    <StatusInfoIcon
                                                        status={
                                                            approvalStatusId
                                                        }
                                                    />
                                                </StatusChip>
                                                {showInfo && (
                                                    <CardRejectionTooltip>
                                                        <CardTooltipBox>
                                                            <StatusBulletList>
                                                                {details.map(
                                                                    (
                                                                        d,
                                                                        idx
                                                                    ) => (
                                                                        <li
                                                                            key={`${photo.id}-d-${idx}`}
                                                                        >
                                                                            {
                                                                                d.text
                                                                            }
                                                                            {d
                                                                                .additional
                                                                                .length >
                                                                                0 && (
                                                                                <ul
                                                                                    style={{
                                                                                        paddingLeft: 16,
                                                                                        marginTop: 4,
                                                                                        listStyleType:
                                                                                            'disc',
                                                                                    }}
                                                                                >
                                                                                    {d.additional.map(
                                                                                        (
                                                                                            a,
                                                                                            i
                                                                                        ) => (
                                                                                            <li
                                                                                                key={
                                                                                                    i
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    a
                                                                                                }
                                                                                            </li>
                                                                                        )
                                                                                    )}
                                                                                </ul>
                                                                            )}
                                                                        </li>
                                                                    )
                                                                )}
                                                            </StatusBulletList>
                                                        </CardTooltipBox>
                                                    </CardRejectionTooltip>
                                                )}
                                            </CardStatusPillWrap>
                                        )}
                                </div>
                            </MetaLeft>
                            <ViewDetailsBtn
                                type='button'
                                onClick={onViewDetailsClick}
                                style={{
                                    marginLeft: 'auto',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                View Details
                            </ViewDetailsBtn>
                        </MetaRow>
                        {photo.requirement?.requiresPullAndClean && (
                            <div
                                style={{
                                    marginTop: 16,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: Config.color.red,
                                }}
                            >
                                Requires Pull and Clean
                            </div>
                        )}
                        {showApprovalButtons && (
                            <ActionRow>
                                <ActionBtn
                                    variant='primary'
                                    disabled={disabledActionFromStatus || busy}
                                    onClick={() => handleApprovePhoto(photo)}
                                >
                                    Approve
                                </ActionBtn>
                                <ActionBtn
                                    variant='danger'
                                    disabled={disabledActionFromStatus || busy}
                                    onClick={() => handleModalOpen('REJECT')}
                                >
                                    Reject
                                </ActionBtn>
                                <ActionBtn
                                    disabled={disabledActionFromStatus || busy}
                                    onClick={() => handleModalOpen('ACCEPT')}
                                >
                                    Accept
                                </ActionBtn>
                            </ActionRow>
                        )}
                        {hasApprovalHistory(photo) && (
                            <ApprovalHistoryTop>
                                {renderApprovalHistory(
                                    photo,
                                    approvalHistory,
                                    'card'
                                )}
                            </ApprovalHistoryTop>
                        )}
                        {showEditApproval && (
                            <EditFullWidthBtn
                                disabled={disabledActionFromStatus || busy}
                                onClick={() =>
                                    handleModalOpen(
                                        photo.approvalStatusId || ''
                                    )
                                }
                                type='button'
                            >
                                Edit
                            </EditFullWidthBtn>
                        )}
                        {showUploadButton && (
                            <div style={{ marginTop: 8 }}>
                                <DropZoneWithNA
                                    id='dropzone-upload'
                                    handleOnDrop={(
                                        acceptedFiles,
                                        rejectedFiles
                                    ) =>
                                        handleOnDrop(
                                            acceptedFiles,
                                            rejectedFiles,
                                            'upload'
                                        )
                                    }
                                    disabled={
                                        disabledActionFromStatus ||
                                        !allowUpload ||
                                        busy
                                    }
                                    text='UPLOAD'
                                />
                            </div>
                        )}
                        {/* stats */}
                        {showReUploadButtons && (
                            <RejectedCtaWrap>
                                <RejectedActionsWrap>
                                    <RejectedPhotoActions
                                        mode='reupload'
                                        busy={busy}
                                        disabledNotApplicable={
                                            disabledActionFromStatus ||
                                            !allowUpload ||
                                            busy
                                        }
                                        disabledUpload={
                                            disabledActionFromStatus || busy
                                        }
                                        handleOnDrop={handleOnDrop}
                                        notApplicableTestId={`not-applicable-${photo.id}`}
                                        uploadTestId={`reupload-${photo.id}`}
                                        uploadText='Re-Upload'
                                        allowFiles={allowFiles}
                                    />
                                </RejectedActionsWrap>
                            </RejectedCtaWrap>
                        )}
                        {/* stats */}
                        {document.documentTypeId ===
                            DOCUMENT_TYPE.WORK_ORDER && (
                            <>
                                {isVideo(photo) ? (
                                    !photo.requirement?.description ? (
                                        <VideoNoDetail />
                                    ) : (
                                        <StatsGrid />
                                    )
                                ) : (
                                    <>
                                        {photo.photoPrediction
                                            ?.classification &&
                                            (() => {
                                                const cls =
                                                    photo.photoPrediction
                                                        .classification;
                                                const pct =
                                                    (cls as any)
                                                        .classificationScore ??
                                                    null;

                                                return (
                                                    <ClassificationRow>
                                                        <ClassificationHeader>
                                                            <StatLabel>
                                                                Classification
                                                            </StatLabel>
                                                            {pct != null && (
                                                                <LabelValue
                                                                    style={{
                                                                        color:
                                                                            pct <
                                                                            50
                                                                                ? Config
                                                                                      .color
                                                                                      .red
                                                                                : Config
                                                                                      .color
                                                                                      .motiliLightBlue,
                                                                    }}
                                                                >
                                                                    {pct}% Clean
                                                                </LabelValue>
                                                            )}
                                                        </ClassificationHeader>
                                                        <CleanDirtyBar>
                                                            <LabelValue>
                                                                Dirty
                                                            </LabelValue>
                                                            <BarTrack>
                                                                <BarFill
                                                                    $pct={
                                                                        pct ?? 0
                                                                    }
                                                                    $invalid={
                                                                        pct !=
                                                                            null &&
                                                                        pct < 50
                                                                    }
                                                                />
                                                            </BarTrack>
                                                            <LabelValue>
                                                                Clean
                                                            </LabelValue>
                                                        </CleanDirtyBar>
                                                    </ClassificationRow>
                                                );
                                            })()}

                                        <StatsGrid>
                                            <div>
                                                <StatLabel>
                                                    Identification
                                                </StatLabel>

                                                <StatValue
                                                    invalid={
                                                        photoIdentification ===
                                                        'FAIL'
                                                    }
                                                    pass={
                                                        photoIdentification ===
                                                        'PASS'
                                                    }
                                                >
                                                    {photoIdentification ===
                                                    'PASS'
                                                        ? 'Pass'
                                                        : photoIdentification ===
                                                            'FAIL'
                                                          ? 'Fail'
                                                          : 'N/A'}
                                                </StatValue>
                                            </div>

                                            <div>
                                                <StatLabel>
                                                    Photo Integrity
                                                </StatLabel>

                                                <StatValue
                                                    invalid={integrityFail}
                                                    pass={
                                                        photo.photoIntegrity
                                                            ?.validIntegrity ===
                                                        true
                                                    }
                                                >
                                                    {photo.photoIntegrity
                                                        ? photo.photoIntegrity
                                                              .validIntegrity
                                                            ? 'Pass'
                                                            : 'Fail'
                                                        : 'N/A'}
                                                </StatValue>
                                            </div>
                                        </StatsGrid>
                                    </>
                                )}
                                {integrityFail && (
                                    <FailureBlock>
                                        <StatLabel>Failure reason</StatLabel>

                                        {/*  Reused Photo */}
                                        {photo.photoIntegrity
                                            ?.validateDuplication
                                            ?.validDuplicate === false && (
                                            <FailureValueRow>
                                                Reused photo
                                                <InfoDotButton
                                                    onClick={
                                                        handleViewInstances
                                                    }
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </FailureValueRow>
                                        )}

                                        {/*  Photo outside timeframe  */}
                                        {photo.photoIntegrity
                                            ?.validateCreationDate
                                            ?.validCreationDate === false &&
                                            (() => {
                                                const capturedDate =
                                                    photo.photoIntegrity
                                                        ?.validateCreationDate
                                                        ?.capturedDate;
                                                const timezone =
                                                    photo.photoMetadata
                                                        ?.metadata
                                                        ?.timezoneId ||
                                                    document.property
                                                        ?.timezone ||
                                                    undefined;
                                                const dateDisplay =
                                                    capturedDate &&
                                                    capturedDate !== 'N/A'
                                                        ? displayDateTimeWithTimezone(
                                                              toDateTimeObject(
                                                                  capturedDate,
                                                                  timezone
                                                              )
                                                          ) || 'N/A'
                                                        : 'N/A';
                                                return (
                                                    <FailureValueRow
                                                        style={{
                                                            flexDirection:
                                                                'column',
                                                            alignItems:
                                                                'flex-start',
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <span>
                                                            Photo outside
                                                            timeframe:
                                                        </span>
                                                        <span
                                                            style={{
                                                                color: Config
                                                                    .color
                                                                    .darkCharcoal,
                                                                fontWeight: 400,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            {dateDisplay}
                                                        </span>
                                                    </FailureValueRow>
                                                );
                                            })()}

                                        {/*  Photo outside radius */}
                                        {photo.photoIntegrity?.validateLocation
                                            ?.validLocation === false &&
                                            (() => {
                                                const addressDisplay =
                                                    photo.photoMetadata
                                                        ?.metadata
                                                        ?.photoAddress ||
                                                    photo.photoIntegrity
                                                        ?.validateLocation
                                                        ?.photoAddress ||
                                                    'Address is not available or not a valid address';
                                                return (
                                                    <FailureValueRow
                                                        style={{
                                                            flexDirection:
                                                                'column',
                                                            alignItems:
                                                                'flex-start',
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <span>
                                                            Photo outside of
                                                            radius:
                                                        </span>
                                                        <span
                                                            style={{
                                                                color: Config
                                                                    .color
                                                                    .darkCharcoal,
                                                                fontWeight: 400,
                                                                fontSize: 13,
                                                            }}
                                                        >
                                                            {addressDisplay}
                                                        </span>
                                                    </FailureValueRow>
                                                );
                                            })()}
                                    </FailureBlock>
                                )}
                            </>
                        )}
                        <PhotoDescription
                            description={photo.requirement?.description}
                        />
                    </PhotoCardShell>
                </AdminCardWrap>
            )}
        </>
    );
};

export default WorkOrderRequirementsPhotosUploadedRow;

interface PhotoParams {
    document: Document;
    isMotiliAccount: boolean;
    isContractorAccount: boolean;
}

const allowUploadPhoto = (params: PhotoParams) => {
    const { document, isMotiliAccount, isContractorAccount } = params;

    const baseDisableStatus = [
        'COMPLETED',
        'CLOSED',
        'CANCELLED',
        'DRAFT_CANCELLED',
    ];

    if (isMotiliAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const disableStatus = [...baseDisableStatus, 'INITIALIZED'];

            return !disableStatus.includes(document.statusId);
        }

        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            return !baseDisableStatus.includes(document.statusId);
        }
    }
    if (isContractorAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const allowStatus = ['IN_PROCESS'];

            return allowStatus.includes(document.statusId);
        }
        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const allowStatus = ['REVISING'];

            return allowStatus.includes(document.statusId);
        }
    }
    return false;
};

const fetchDuplicateRefDocs = async (photoIntegrity: any | undefined) => {
    const dupList = photoIntegrity?.validateDuplication?.duplicateList;

    const docIds = dupList
        ?.filter((m: { refDocumentId: string }) => m.refDocumentId)
        ?.map((m: { refDocumentId: string }) => m.refDocumentId);

    if (!Array.isArray(docIds) || docIds.length === 0) return [];

    const docs = await getUsagePhotoByDocIds(docIds);

    return docs.filter((doc: { id: string; documentTypeId: DOCUMENT_TYPE }) =>
        dupList?.find(
            (m: { refDocumentId?: string; refDocumentType?: string }) =>
                m.refDocumentId === doc.id &&
                m.refDocumentType === doc.documentTypeId
        )
    );
};

function ApprovalHistoryDetail({
    photo,
    approvalReasons,
}: {
    photo: Photo;
    approvalReasons: ApprovalReason[];
}) {
    const approval = photo.approval || {};
    const reasons = filter(approvalReasons, reason =>
        includes(approval?.reasons, reason.id)
    ).map(reason => ({
        id: reason.id,
        display: reason.id === 'OTHER' ? approval.reasonOther : reason.display,
        additionalReasons: filter(reason.additionalReasons || [], additional =>
            includes(approval.additionalReasons, additional.id)
        ),
    }));
    return (
        <div className={styles.callout}>
            <h5>
                {`${
                    ACTION[photo.action]?.display || photo.action
                } - ${displayDateTime(photo.created)}`}
            </h5>
            <div className={styles.approvalContainer}>
                {reasons.map(reason => (
                    <div
                        key={reason.id}
                        className={styles.approvalReason}
                        style={{ paddingLeft: 0, marginLeft: 0 }}
                    >
                        <div className={styles.approvalTitle}>
                            {reason.display}
                        </div>
                        {reason.additionalReasons.length > 0 && (
                            <div>
                                {reason.additionalReasons.map(addition => (
                                    <div
                                        key={addition.id}
                                        className={styles.approvalReason}
                                    >
                                        <div>• {addition.display}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                {approval.additionalNotes && (
                    <div className={styles.additionalNotes}>
                        {Object.prototype.hasOwnProperty.call(
                            APPROVAL_STATUS,
                            photo.action
                        ) &&
                            (photo.action === 'APPROVE' ||
                                photo.action === 'REJECT' ||
                                photo.action === 'ACCEPT') &&
                            APPROVAL_STATUS[photo.action] && (
                                <div className={styles.approvalTitle}>
                                    Additional Comments
                                </div>
                            )}
                        {approval.additionalNotes}
                    </div>
                )}
            </div>
        </div>
    );
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        fontSize: '14px',
        fontWeight: 400,
        color: Config.color.darkCharcoal,
        backgroundColor: Config.color.white,
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)', // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        maxWidth: '400px',
        padding: '16px',
    },
}));

const StyledPhotoDescriptionContainer = styled.div`
    margin-top: 16px;
    .${tooltipClasses.tooltip} {
        font-size: 16px;
        font-weight: 400;
        color: ${Config.color.darkCharcoal};
    }
`;

const StyledPhotoDescriptionValue = styled(StatValue)`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: break-word;
    font-size: 14px;
`;

export const PhotoDescription = ({
    description,
    style,
}: {
    description?: string;
    style?: CSSProperties;
}) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const checkOverflow = () => {
        const element = textRef.current;
        if (element) {
            const hasOverFlow = element.scrollHeight > element.clientHeight;
            setIsOverflowing(hasOverFlow);
        }
    };

    useLayoutEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [description]);

    if (!description || isEmpty(description?.trim())) {
        return;
    }

    return (
        <StyledPhotoDescriptionContainer style={style}>
            <StatLabel>Description</StatLabel>
            {isOverflowing ? (
                <LightTooltip
                    title={description}
                    disableHoverListener={!isOverflowing}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -14],
                                    },
                                },
                            ],
                        },
                    }}
                >
                    <StyledPhotoDescriptionValue ref={textRef}>
                        {description}
                    </StyledPhotoDescriptionValue>
                </LightTooltip>
            ) : (
                <StyledPhotoDescriptionValue ref={textRef}>
                    {description}
                </StyledPhotoDescriptionValue>
            )}
        </StyledPhotoDescriptionContainer>
    );
};

export const VideoMask = ({ photo, duration, isShowPlayIcon = true }) => {
    if (!isVideo(photo)) return '';

    switch (photo.requirement?.video?.statusId) {
        case VideoStatusId.ERROR:
            return <VideoErrorMask />;
        case VideoStatusId.IN_PROCESS:
            return <VideoInProcessMask />;
        default:
            return (
                <VideoPlayMask
                    duration={duration}
                    isShowPlayIcon={isShowPlayIcon}
                />
            );
    }
};

export type MediaCategory = 'video' | 'photo' | 'document';

export const getMediaCategory = (p: Photo): MediaCategory => {
    const mime = p?.requirement?.mimeType || '';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('image/')) return 'photo';
    return 'document';
};

export const isIncompleteVideo = (photo: Photo): boolean =>
    isVideo(photo) && !isCompleteVideo(photo);

export interface ShowKebabParams {
    photo: Photo;
    disabled?: boolean;
    disabledActionFromStatus: boolean;
    authenticatedUserCanEdit: boolean;
    disableActionIssueTracking?: boolean;
    removeAcls?: boolean;
    busy: boolean;
}

export const getShowKebab = (params: ShowKebabParams): boolean => {
    const {
        photo,
        disabled,
        disabledActionFromStatus,
        authenticatedUserCanEdit,
        removeAcls,
        busy,
        disableActionIssueTracking = false,
    } = params;

    return (
        Boolean(photo?.requirement?.url) &&
        !disabled &&
        !disabledActionFromStatus &&
        authenticatedUserCanEdit &&
        !disableActionIssueTracking &&
        removeAcls !== false &&
        !busy
    );
};

export interface IntegrityDetail {
    label: string;
    value?: string;
    showInstancesBtn?: boolean;
}

export function getIntegrityDetails(
    photoIntegrity: any,
    photoMetadata: any,
    propertyTimezone?: string
): IntegrityDetail[] {
    if (!photoIntegrity || photoIntegrity.validIntegrity !== false) return [];

    const items: IntegrityDetail[] = [];

    if (photoIntegrity.validateDuplication?.validDuplicate === false) {
        items.push({ label: 'Reused photo', showInstancesBtn: true });
    }

    if (photoIntegrity.validateCreationDate?.validCreationDate === false) {
        const capturedDate = photoIntegrity.validateCreationDate?.capturedDate;
        const timezone =
            photoMetadata?.metadata?.timezoneId ||
            propertyTimezone ||
            undefined;
        const dateDisplay =
            capturedDate && capturedDate !== 'N/A'
                ? displayDateTimeWithTimezone(
                      toDateTimeObject(capturedDate, timezone)
                  ) || 'N/A'
                : 'N/A';
        items.push({ label: 'Photo outside timeframe', value: dateDisplay });
    }

    if (photoIntegrity.validateLocation?.validLocation === false) {
        const addressDisplay =
            photoMetadata?.metadata?.photoAddress ||
            photoIntegrity.validateLocation?.photoAddress ||
            'Address is not available or not a valid address';
        items.push({ label: 'Photo outside of radius', value: addressDisplay });
    }

    return items;
}

export type StatusDetail = {
    text: string;
    additional: string[];
};

export const getStatusDetails = (
    p: Photo,
    workOrderRequirementApprovalReasons: any[]
): StatusDetail[] => {
    const approval = (p as any)?.approval || {};
    const reasonIds: string[] = approval?.reasons || [];
    const additionalIds: string[] = approval?.additionalReasons || [];
    const notes: string = approval?.additionalNotes || '';

    const reasons = (workOrderRequirementApprovalReasons || [])
        .filter(r => reasonIds.includes(r.id))
        .map(r => {
            const base = r.id === 'OTHER' ? approval?.reasonOther : r.display;
            const additional = (r.additionalReasons || [])
                .filter(a => additionalIds.includes(a.id))
                .map(a => a.display)
                .filter(Boolean);
            return { text: base, additional };
        })
        .filter(r => Boolean(r.text)) as StatusDetail[];

    if (notes) {
        reasons.push({ text: notes, additional: [] });
    }

    return reasons;
};

export const VideoPlayMask = ({
    duration,
    isShowPlayIcon = true,
    disabled = false,
}: {
    duration?: string;
    isShowPlayIcon: boolean;
    disabled: boolean;
}) => {
    return (
        <StyledVideoMaskContainer>
            {isShowPlayIcon && (
                <StyledVideoPlayMarkContainer>
                    <StyledPlayVideoWrapper>
                        <PlayIconButton
                            disabled={disabled}
                            iconColor={Config.color.white}
                            sx={{ fontSize: 50 }}
                        />
                    </StyledPlayVideoWrapper>
                </StyledVideoPlayMarkContainer>
            )}
            {duration && (
                <StyledVideoDurationContainer>
                    <StyledVideoDuration>{duration}</StyledVideoDuration>
                </StyledVideoDurationContainer>
            )}
        </StyledVideoMaskContainer>
    );
};

export const VideoErrorMask = () => {
    return <StyledVideoErrorMarkContainer>Error</StyledVideoErrorMarkContainer>;
};

export const VideoInProcessMask = () => {
    return (
        <StyledVideoErrorMarkContainer>
            Processing
        </StyledVideoErrorMarkContainer>
    );
};

export const VideoNoDetail = () => (
    <div
        style={{
            marginTop: 10,
            width: '100%',
            flex: 1,
            alignSelf: 'stretch',
            minHeight: 40,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 8,
            background: 'var(--color-super-light-gray, #F5F5F5)',
            fontSize: 12,
            color: Config.color.localGray,
        }}
    >
        N/A
    </div>
);

export interface DisablePhotoRequirementParams {
    document: Document;
    isMotiliAccount: boolean;
    isContractorAccount: boolean;
    accountId?: number;
}

export const getIsActionDisabledFromStatus = (
    params: DisablePhotoRequirementParams
): boolean => {
    const { document, isMotiliAccount, isContractorAccount, accountId } =
        params;

    if (isMotiliAccount) {
        const disableStatus = ['COMPLETED', 'CLOSED', 'CANCELLED'];
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            disableStatus.includes(document.statusId)
        );
    }
    if (isContractorAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const disableStatus = [
                'COMPLETED',
                'CLOSED',
                'CANCELLED',
                'IN_REVIEW',
            ];
            const isCheckIn = document.checkInsV2?.some(
                checkIn =>
                    !checkIn.checkedOutAt &&
                    checkIn.checkedInUserId === accountId
            );
            return disableStatus.includes(document.statusId) || !isCheckIn;
        }
        const disableStatus = ['COMPLETED', 'CLOSED', 'CANCELLED', 'IN_REVIEW'];
        return (
            document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            disableStatus.includes(document.statusId)
        );
    }
    return true;
};

const TERMINAL_STATUSES = [
    'CANCELLED',
    'CLOSED',
    'COMPLETED',
    'DRAFT_CANCELLED',
] as const;

export const isTerminalStatus = (document: Document): boolean => {
    return TERMINAL_STATUSES.includes(document.statusId as any);
};

export const isCompleteVideo = photo =>
    isVideo(photo) &&
    photo.requirement?.video?.statusId === VideoStatusId.COMPLETE ||
    photo.requirement?.thumbnail?.statusId === VideoStatusId.COMPLETE;

const CssOverlayCenter = css`
    display: flex;
    position: absolute;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    top: 0;
`;

const StyledPlayVideoWrapper = styled.div`
    width: 50px;
    height: 50px;
    display: flex;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    background-color: ${Config.color.motiliLightBlue};
`;

const StyledVideoMaskContainer = styled.div`
    display: flex;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
`;

const StyledVideoPlayMarkContainer = styled.div`
    ${CssOverlayCenter};
`;

const StyledVideoErrorMarkContainer = styled.div`
    ${CssOverlayCenter};
    color: ${Config.color.white};
`;

const StyledVideoDurationContainer = styled.div`
    display: flex;
    position: absolute;
    height: 100%;
    width: 100%;
    align-items: flex-end;
    top: 0;
`;
const StyledVideoDuration = styled.span`
    color: ${Config.color.black};
    background: ${Config.color.white};
    margin-left: 6px;
    margin-bottom: 5px;
    padding: 4px;
    font-weight: 700;
    border-radius: 2px;
`;

export const openVideoInNewTab = async (photo: Photo) => {
    const videoUrlResponse = await getSecuredURL(photo.requirement?.video);
    window.open(videoUrlResponse, '_blank');
};
